import { useState, useEffect } from 'react';
import { placeOrder } from '../../services/OrdersService';
import { getBalance, doSwap } from '../../services/ExchangeService';
import { getSymbol } from '../../services/SymbolsService';
import Toast from '../../components/Toast';
import './BuyWizard.css';

function BuyWizard({ coin, price: initialPrice, onClose, onSuccess }) {
    const isCustody = localStorage.getItem('isCustody') === 'true';
    const quoteAsset = isCustody ? 'USDC' : 'USDT';
    const ledgerAsset = isCustody ? 'USDC' : 'USDT';

    const [step, setStep] = useState(1);
    const [brlAmount, setBrlAmount] = useState('');
    const [quantity, setQuantity] = useState('0');
    const [totalUSDT, setTotalUSDT] = useState('0');
    const [orderType] = useState('MARKET');
    const [limitPrice] = useState('');
    const [price, setPrice] = useState(initialPrice);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [notification, setNotification] = useState({ type: '', text: '' });
    const [usdtBalance, setUsdtBalance] = useState(0);
    const [walletAssets, setWalletAssets] = useState([]);
    const [assetPrices, setAssetPrices] = useState({});
    const [walletBrl, setWalletBrl] = useState([]);
    const [totalBrlAvailable, setTotalBrlAvailable] = useState(0);
    const [selectedAsset, setSelectedAsset] = useState(isCustody ? 'USDC' : '');
    const [usdtBrlRate, setUsdtBrlRate] = useState(0);
    const [minNotional, setMinNotional] = useState(0);
    const [minBrl, setMinBrl] = useState(0);

    const symbol = `${coin}${quoteAsset}`;

    useEffect(() => {
        let isMounted = true;

        const fetchPrice = async () => {
            try {
                if (coin === 'USDT' || coin === 'USDC' || coin === 'BUSD') {
                    if (isMounted) setPrice(1);
                    return;
                }
                const response = await fetch(`https://api.binance.com/api/v3/ticker/price?symbol=${symbol}`);
                const data = await response.json();
                const nextPrice = parseFloat(data.price || 0);
                if (isMounted && nextPrice > 0) {
                    setPrice(nextPrice);
                    return;
                }
                if (coin === 'HYPE') {
                    const responseCg = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=hyperliquid&vs_currencies=usd');
                    const dataCg = await responseCg.json();
                    const cgPrice = parseFloat(dataCg?.hyperliquid?.usd || 0);
                    if (isMounted && cgPrice > 0) setPrice(cgPrice);
                }
            } catch (err) {
                console.error('Erro ao buscar preço:', err);
            }
        };

        fetchPrice();
        const interval = setInterval(fetchPrice, 5000);

        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, [symbol]);

    useEffect(() => {
        loadWalletAssets();
    }, []);

    useEffect(() => {
        const loadSymbolInfo = async () => {
            try {
                const data = await getSymbol(symbol);
                const minValue = parseFloat(data?.minNotional || 0);
                setMinNotional(minValue);
            } catch (err) {
                console.error('Erro ao carregar minNotional:', err);
                setMinNotional(0);
            }
        };
        loadSymbolInfo();
    }, [symbol]);

    useEffect(() => {
        const computedPrice = parseFloat(price || 0);
        const normalizedBrl = (brlAmount || '').toString().replace(',', '.');
        const brlValue = parseFloat(normalizedBrl || 0);
        const rate = parseFloat(usdtBrlRate || 0);

        if (brlValue > 0 && rate > 0 && computedPrice > 0) {
            const usdtValue = brlValue / rate;
            const qtyValue = usdtValue / computedPrice;
            setTotalUSDT(usdtValue.toFixed(2));
            setQuantity(qtyValue.toFixed(8));
        } else {
            setTotalUSDT('0');
            setQuantity('0');
        }
    }, [brlAmount, price, limitPrice, orderType, usdtBrlRate]);

    async function loadWalletAssets() {
        try {
            const balance = await getBalance(false);
            let usdt = 0;
            const assets = [];

            if (Array.isArray(balance)) {
                balance.forEach(item => {
                    const asset = item.asset || item.symbol;
                    const available = parseFloat(item.available || item.availableBalance || 0);
                    if (!asset || available <= 0) return;
                    assets.push({ asset, available });
                    if (asset === ledgerAsset) usdt = available;
                });
            } else {
                Object.entries(balance).forEach(([key, value]) => {
                    if (key === 'fiatEstimate') return;
                    const available = parseFloat(value.available || value.availableBalance || 0);
                    if (available <= 0) return;
                    assets.push({ asset: key, available });
                    if (key === ledgerAsset) usdt = available;
                });
            }

            setUsdtBalance(usdt);
            setWalletAssets(assets);
            if (isCustody) setSelectedAsset('USDC');
            return { assets, usdt };
        } catch (err) {
            console.error('Erro ao carregar saldo USDT:', err);
            return { assets: [], usdt: 0 };
        }
    }

    useEffect(() => {
        if (walletAssets.length === 0) return;

        const fetchPrices = async () => {
            const nextPrices = {};
            await Promise.all(walletAssets.map(async ({ asset }) => {
                if (asset === 'USDT' || asset === 'USDC' || asset === 'BUSD') {
                    nextPrices[asset] = 1;
                    return;
                }
                try {
                    const response = await fetch(`https://api.binance.com/api/v3/ticker/price?symbol=${asset}USDT`);
                    const data = await response.json();
                    const price = parseFloat(data.price || 0);
                    if (price > 0) nextPrices[asset] = price;
                } catch (err) {
                    console.error(`Erro ao buscar preço de ${asset}:`, err);
                }
            }));
            setAssetPrices(nextPrices);
        };

        fetchPrices();
    }, [walletAssets]);

    useEffect(() => {
        if (walletAssets.length === 0 || usdtBrlRate <= 0) {
            setWalletBrl([]);
            setTotalBrlAvailable(0);
            return;
        }

        const brlList = walletAssets.map(({ asset, available }) => {
            const priceUSDT = assetPrices[asset] || 0;
            const brlValue = priceUSDT > 0 ? available * priceUSDT * usdtBrlRate : 0;
            return { asset, available, brlValue };
        });

        const total = brlList.reduce((sum, item) => sum + (item.brlValue || 0), 0);
        setWalletBrl(brlList);
        setTotalBrlAvailable(total);
    }, [walletAssets, assetPrices, usdtBrlRate]);

    useEffect(() => {
        if (minNotional > 0 && usdtBrlRate > 0) {
            setMinBrl(minNotional * usdtBrlRate);
        } else {
            setMinBrl(0);
        }
    }, [minNotional, usdtBrlRate]);

    async function loadUsdtBrlRate() {
        try {
            const response = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=USDTBRL');
            const data = await response.json();
            const rate = parseFloat(data.price || 0);
            if (rate > 0) {
                setUsdtBrlRate(rate);
                return;
            }
        } catch (err) {
            console.error('Erro ao carregar taxa USDT/BRL (Binance):', err);
        }

        try {
            const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=tether&vs_currencies=brl');
            const data = await response.json();
            const rate = parseFloat(data?.tether?.brl || 0);
            if (rate > 0) {
                setUsdtBrlRate(rate);
            }
        } catch (err) {
            console.error('Erro ao carregar taxa USDT/BRL (CoinGecko):', err);
        }
    }

    useEffect(() => {
        loadUsdtBrlRate();
        const interval = setInterval(loadUsdtBrlRate, 15000);
        return () => clearInterval(interval);
    }, []);

    function handleBrlChange(e) {
        const value = e.target.value;
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setBrlAmount(value);
        }
    }

    function handleNext() {
        if (step === 1) {
            if (!selectedAsset) {
                setError('Selecione a moeda que deseja usar na compra.');
                return;
            }
            const normalizedBrl = (brlAmount || '').toString().replace(',', '.');
            if (!brlAmount || parseFloat(normalizedBrl) <= 0) {
                setError('Por favor, informe o valor em reais que deseja usar na compra.');
                return;
            }
            if (!usdtBrlRate || usdtBrlRate <= 0) {
                setError('Não foi possível obter a cotação USDT/BRL no momento.');
                return;
            }
            const selectedInfo = walletBrl.find(item => item.asset === selectedAsset);
            const selectedBrlAvailable = selectedInfo ? selectedInfo.brlValue : 0;

            if (parseFloat(normalizedBrl) > selectedBrlAvailable) {
                setError('Saldo em reais insuficiente na moeda selecionada.');
                return;
            }
            if (minBrl > 0 && parseFloat(normalizedBrl) < minBrl) {
                setError(`Valor mínimo para compra: R$ ${minBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.`);
                return;
            }
            setError('');
            setStep(2);
        }
    }

    function handleBack() {
        if (step > 1) {
            setStep(step - 1);
            setError('');
        }
    }

    async function ensureUsdtBalance(requiredUSDT) {
        if (!selectedAsset) return false;

        let currentAssets = walletAssets;
        let currentQuote = usdtBalance;

        if (currentAssets.length === 0) {
            const refreshed = await loadWalletAssets();
            currentAssets = refreshed.assets;
            currentQuote = refreshed.usdt;
        }

        // Custódia: gasta USDC direto no par *USDC, sem swap para USDT.
        if (isCustody) {
            const usdc = currentAssets.find(item => item.asset === 'USDC');
            const available = usdc ? usdc.available : currentQuote;
            return requiredUSDT <= available + 1e-8;
        }

        if (selectedAsset === 'USDT') return requiredUSDT <= currentQuote;

        const assetInfo = currentAssets.find(item => item.asset === selectedAsset);
        if (!assetInfo) return false;

        let priceUSDT = assetPrices[selectedAsset] || 0;
        if (selectedAsset === 'USDC' || selectedAsset === 'BUSD') priceUSDT = 1;
        if (priceUSDT <= 0) return false;

        const assetUsdtValue = assetInfo.available * priceUSDT;
        if (requiredUSDT > assetUsdtValue && selectedAsset !== 'USDT') {
            return false;
        }

        try {
            const amountToSell = requiredUSDT / priceUSDT;
            await doSwap(selectedAsset, 'USDT', amountToSell);
            const refreshed = await loadWalletAssets();
            currentQuote = refreshed.usdt;
        } catch (err) {
            console.error(`Erro ao converter ${selectedAsset} para USDT:`, err);
        }

        return currentQuote >= requiredUSDT;
    }

    async function handleConfirm() {
        try {
            setLoading(true);
            setError('');

            const requiredQuote = parseFloat(Number(totalUSDT || 0).toFixed(2));
            if (!requiredQuote || requiredQuote <= 0) {
                setError(`Valor em ${quoteAsset} inválido para a compra.`);
                return;
            }

            const ok = await ensureUsdtBalance(requiredQuote);
            if (!ok) {
                const minText = minBrl > 0
                    ? ` Valor mínimo para compra: R$ ${minBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.`
                    : '';
                setError(isCustody
                    ? `Saldo USDC de custódia insuficiente.${minText}`
                    : `Saldo insuficiente para converter e executar a compra.${minText}`);
                return;
            }

            const order = {
                symbol: symbol,
                side: 'BUY',
                quantity: null,
                limitPrice: null,
                options: {
                    type: 'MARKET',
                    quoteOrderQty: requiredQuote
                }
            };

            await placeOrder(order, false);
            setNotification({ type: 'success', text: 'Ordem de compra criada com sucesso!' });
            setTimeout(() => {
                onSuccess();
            }, 1500);
        } catch (err) {
            const errorMsg = err.response?.data?.message || 
                           err.response?.data?.msg || 
                           err.message || 
                           'Erro ao criar ordem de compra';
            setError(errorMsg);
            setNotification({ type: 'error', text: errorMsg });
        } finally {
            setLoading(false);
        }
    }

    return (
        <main className="content">
            <div className="buy-wizard-container">
                <div className="buy-wizard-header">
                    <h1>Comprar {coin}</h1>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="wizard-progress">
                    <div className={`wizard-step ${step >= 1 ? 'active' : ''}`}>
                        <div className="step-number">1</div>
                        <div className="step-label">Configurar Ordem</div>
                    </div>
                    <div className="wizard-line"></div>
                    <div className={`wizard-step ${step >= 2 ? 'active' : ''}`}>
                        <div className="step-number">2</div>
                        <div className="step-label">Confirmar</div>
                    </div>
                </div>

                {step === 1 && (
                    <div className="wizard-content">
                        <div className="wizard-section">
                            <h3>Passo 1: Configure sua ordem de compra</h3>
                            <p className="wizard-description">
                                Vamos usar o saldo disponível e converter para reais. Informe quanto deseja usar na compra.
                            </p>

                            <div className="summary-box">
                                <div className="summary-row">
                                    <span>Saldo total em reais (todas as moedas):</span>
                                    <strong>
                                        {totalBrlAvailable > 0
                                            ? `R$ ${totalBrlAvailable.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                            : '—'}
                                    </strong>
                                </div>
                                <div className="summary-row">
                                    <span>Saldo disponível ({ledgerAsset}):</span>
                                    <strong>${parseFloat(usdtBalance || 0).toLocaleString('pt-BR', { 
                                        minimumFractionDigits: 2, 
                                        maximumFractionDigits: 2 
                                    })}</strong>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Escolha a moeda que deseja usar para pagar</label>
                                <select
                                    className="form-input"
                                    value={selectedAsset}
                                    onChange={(e) => setSelectedAsset(e.target.value)}
                                    disabled={isCustody}
                                >
                                    <option value="">Selecione...</option>
                                    {(isCustody ? walletBrl.filter(item => item.asset === 'USDC') : walletBrl).map(item => (
                                        <option key={item.asset} value={item.asset}>
                                            {item.asset} · {item.available.toFixed(8)} ({item.brlValue > 0
                                                ? `R$ ${item.brlValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                : '—'})
                                        </option>
                                    ))}
                                </select>
                                {isCustody ? <small className="text-muted">Custódia: pagamento e execução sempre em USDC ({symbol}).</small> : null}
                            </div>

                            <div className="form-group">
                                <label>Tipo de Ordem</label>
                                <div className="order-type-buttons">
                                    <button
                                        className="order-type-btn active"
                                        type="button"
                                        onClick={(e) => e.preventDefault()}
                                    >
                                        <strong>Market</strong>
                                        <span>Compra imediata ao preço de mercado</span>
                                    </button>
                                </div>
                            </div>

                            <div className="price-display">
                                <div className="price-label">Preço Atual ({coin}/{quoteAsset})</div>
                                <div className="price-value">${parseFloat(price || 0).toLocaleString('pt-BR', { 
                                    minimumFractionDigits: 2, 
                                    maximumFractionDigits: 8 
                                })}</div>
                            </div>

                            <div className="form-group">
                                <label>Valor em reais que deseja usar</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={brlAmount}
                                    onChange={handleBrlChange}
                                    placeholder="0,00"
                                />
                            </div>

                            {selectedAsset && (
                                <div className="summary-box">
                                    {walletBrl.filter(item => item.asset === selectedAsset).map(item => (
                                        <div className="summary-row" key={item.asset}>
                                            <span>{item.asset}: {parseFloat(item.available || 0).toFixed(8)}</span>
                                            <span>
                                                {item.brlValue > 0
                                                    ? `R$ ${item.brlValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                    : '—'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="summary-box">
                                <div className="summary-row">
                                    <span>Total em {quoteAsset}:</span>
                                    <strong>${parseFloat(totalUSDT || 0).toLocaleString('pt-BR', { 
                                        minimumFractionDigits: 2, 
                                        maximumFractionDigits: 2 
                                    })}</strong>
                                </div>
                                <div className="summary-row">
                                    <span>Quantidade estimada ({coin}):</span>
                                    <span>{parseFloat(quantity || 0).toFixed(8)} {coin}</span>
                                </div>
                            </div>

                            {error && <div className="error-message">{error}</div>}
                        </div>

                        <div className="wizard-actions">
                            <button className="btn-secondary" onClick={onClose}>Cancelar</button>
                            <button className="btn-primary" onClick={handleNext}>Próximo</button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="wizard-content">
                        <div className="wizard-section">
                            <h3>Passo 2: Confirme sua ordem</h3>
                            <p className="wizard-description">
                                Revise os detalhes da sua ordem antes de confirmar.
                            </p>

                            <div className="confirmation-box">
                                <div className="confirmation-row">
                                    <span>Moeda:</span>
                                    <strong>{coin}</strong>
                                </div>
                                <div className="confirmation-row">
                                    <span>Tipo de Ordem:</span>
                                    <strong>Market</strong>
                                </div>
                                <div className="confirmation-row">
                                    <span>Valor em reais:</span>
                                    <strong>R$ {parseFloat(brlAmount || 0).toLocaleString('pt-BR', { 
                                        minimumFractionDigits: 2, 
                                        maximumFractionDigits: 2 
                                    })}</strong>
                                </div>
                                <div className="confirmation-row">
                                    <span>Total em {quoteAsset}:</span>
                                    <strong>${parseFloat(totalUSDT || 0).toLocaleString('pt-BR', { 
                                        minimumFractionDigits: 2, 
                                        maximumFractionDigits: 2 
                                    })} {quoteAsset}</strong>
                                </div>
                                <div className="confirmation-row">
                                    <span>Quantidade estimada:</span>
                                    <strong>{parseFloat(quantity || 0).toFixed(8)} {coin}</strong>
                                </div>
                            </div>

                            {error && <div className="error-message">{error}</div>}
                        </div>

                        <div className="wizard-actions">
                            <button className="btn-secondary" onClick={handleBack}>Voltar</button>
                            <button 
                                className="btn-primary" 
                                onClick={handleConfirm}
                                disabled={loading}
                            >
                                {loading ? 'Processando...' : 'Confirmar Compra'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
            {notification.text && (
                <Toast
                    type={notification.type}
                    text={notification.text}
                    onClose={() => setNotification({ type: '', text: '' })}
                />
            )}
        </main>
    );
}

export default BuyWizard;

